import { useState, useEffect, useCallback, useMemo } from 'react';
import AppShell from '../components/AppShell';
import { centerShipperAPI, centerDailyAPI } from '../api/client';
import { alertDialog, confirmDialog, toast } from '../components/AppDialog';
import { Page, PageHeader, Pill, Button } from '../components/ui';
import { COLORS, TONES, card as uiCard, input as uiInput, btn as uiBtn, ICON_STROKE } from '../utils/uiStyles';
import { IconTrendingUp, IconChevronRight, IconChevronDown, IconDownload, IconRefresh, IconArrowUp, IconArrowDown } from '@tabler/icons-react';
import { fmtKstDateTime, kstDaysAgo } from '../utils/kstTime';

/* 센터 일별 실적 — 입력 + 실적요약 (마스터 2026-08-20, 설계 patch_center_daily_report 3~5단계)
 * · 근무·도급은 WMS 에 없는 값이라 여기서 받는다. 금액을 비우면 센터 기본값이 들어간다.
 * · 2.0 고객사는 매일 06:00 자동 집계, 1.0 고객사는 수기 입력.
 * · 🔴 자동 대상인데 아직 안 돈 행은 0 이 아니라 '미집계'로 그린다 — 실제 0 과 구분해야 한다.
 * · 일자 «마감»(데일리보고 1단계 ①) — 마감된 날은 입력칸·저장·재집계가 잠긴다. 고치려면 «마감 해제».
 *   🔴 `get` 은 마운트·탭 전환·저장/마감 뒤에만 부른다. `history` 는 «수정 이력»을 펼쳤을 때만 부른다.
 *   🔴 유형은 <select> 가 아니라 단추다(입력 탭의 select 는 센터 하나뿐 — CenterDailyPage.test 가 단정).
 * · 도급사별 인원(데일리보고 1단계 ②b) — 기준(평일/휴일)·도급사도 단추. 저장은 **전 도급사** 행을 한 번에 보낸다
 *   (서버가 그날 행을 몸통으로 통째 바꾼다 — 고른 도급사만 보내면 나머지가 지워진다). 도급사 행이 있는 날은
 *   «근무 · 도급»의 도급 3칸이 잠긴다(정본은 도급사 입력). 도급사·단가 관리 카드는 관리자에게만, 펼칠 때만 읽는다.
 */
// 🔴 KST 기준 어제 — UTC 날짜로 만들면 KST 00~09시에 그제가 뜬다(2026-10-03 KST 일괄 전환).
const yesterday = () => kstDaysAgo(1);
const num = (v) => (v === null || v === undefined || v === '' ? '' : v);
const dash = (v) => (v === null || v === undefined ? '—' : v);
// 응답에 note_types 가 없을 수도 있다(구버전 서버) — 같은 목록으로 그린다
const DEFAULT_NOTE_TYPES = ['물동량', '인원', '시스템', '기타'];
const KIND_LABEL = { labor: '근무', shipment: '출고', close: '마감', reopen: '해제' };
const FIELD_LABEL = {
  reg_persons: '정규 인원', con_persons: '도급 인원', reg_hours: '정규 시간', con_hours: '도급 시간',
  con_cost: '도급 금액', note: '특이사항', note_type: '특이사항 유형', normal_cnt: '일반출고', guarantee_cnt: '도착보장',
  day_kind: '평일/휴일',
};
const histTarget = (h) => {
  if (h.kind === 'shipment') return `${h.shipper_name || '(삭제된 고객사)'} · ${FIELD_LABEL[h.field] || h.field || ''}`;
  // 도급사 행 이력의 field 는 이미 «(주간)일용직 일반·인원» 꼴이다
  if (h.kind === 'labor' && h.contractor_name) return `${h.contractor_name} · ${h.field}`;
  if (h.kind === 'labor') return FIELD_LABEL[h.field] || h.field || '—';
  return '—';
};

// ── 도급사별 인원(②b) ── 연장은 숫자면 ot_hours, 그 밖(«=19*5-1*1»·«10-2»)은 원식 그대로 ot_expr — 계산은 서버
const OT_NUM = /^-?\d+(\.\d+)?$/;
const EMPTY_CON = { grades: [], contractors: [] };
const conInputFrom = (rows) => {
  const out = {};
  (rows || []).forEach((r) => {
    if (!out[r.contractor_id]) out[r.contractor_id] = {};
    out[r.contractor_id][r.grade] = {
      persons: r.persons === null || r.persons === undefined ? '' : String(r.persons),
      ot: r.ot_expr || (r.ot_hours === null || r.ot_hours === undefined ? '' : String(r.ot_hours)),
    };
  });
  return out;
};
const conPersons = (v) => Math.trunc(Number(v)) || 0;   // 소수가 가면 422(detail 이 배열)라 정수로 자른다
// 화면 상태 {contractor_id: {grade: {persons, ot}}} → 몸통 rows. 인원·연장 둘 다 비었거나 0 인 칸은 빼고 보낸다.
const conRowsOut = (input) => {
  const rows = [];
  Object.entries(input).forEach(([cid, grades]) => {
    Object.entries(grades || {}).forEach(([grade, v]) => {
      const persons = conPersons(v.persons);
      const ot = String(v.ot ?? '').trim();
      const otNum = ot === '' ? 0 : (OT_NUM.test(ot) ? Number(ot) : null);
      if (persons === 0 && otNum === 0) return;
      rows.push(otNum === null
        ? { contractor_id: Number(cid), grade, persons, ot_hours: 0, ot_expr: ot }
        : { contractor_id: Number(cid), grade, persons, ot_hours: otNum, ot_expr: null });
    });
  });
  return rows;
};
const rateOf = (k, grade, dayKind) => {
  const r = k?.rates?.[grade];
  const v = r ? (dayKind === '휴일' ? r.holiday : r.weekday) : null;
  return v === undefined ? null : v;
};
// 미리보기 — 정본은 저장 뒤 서버가 계산한 contractor_rows[].cost
const costPreview = (v, rate) => {
  if (rate === null || !v) return '';
  const ot = String(v.ot ?? '').trim();
  if (ot !== '' && !OT_NUM.test(ot)) return '저장 시 계산';
  const persons = conPersons(v.persons);
  const hours = ot === '' ? 0 : Number(ot);
  if (persons === 0 && hours === 0) return '';
  return `${Math.round(persons * rate + (hours * rate) / 8).toLocaleString()}원`;
};
const ratesForm = (grades, k) => Object.fromEntries((grades || []).map((g) => [g, {
  weekday: num(k?.rates?.[g]?.weekday), holiday: num(k?.rates?.[g]?.holiday),
}]));
const rateIn = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const histChange = (h) => (h.kind === 'labor' || h.kind === 'shipment'
  ? `${h.old_value ?? '—'} → ${h.new_value ?? '—'}` : '—');

const card = { ...uiCard, padding: '14px', marginBottom: '12px' };
const th = { height: '33px', padding: '5px 9px', textAlign: 'left', fontSize: '12px', fontWeight: 400, color: COLORS.textMuted, whiteSpace: 'nowrap', borderBottom: `1px solid ${COLORS.borderSoft}` };
const td = { height: '41px', padding: '7px 9px', fontSize: '13px', color: COLORS.textSub, borderBottom: `1px solid ${COLORS.borderSoft}` };
// 각 표의 첫 열 — 값은 th/td 와 같고 왼쪽 패딩만 4(시안 .database :first-child). shorthand 와 paddingLeft 를 한 객체에 섞지 않는다.
const thFirst = { textAlign: 'left', height: '33px', paddingTop: '5px', paddingRight: '9px', paddingBottom: '5px', paddingLeft: '4px', borderBottom: `1px solid ${COLORS.borderSoft}`, color: COLORS.textMuted, fontWeight: 400, fontSize: '12px', whiteSpace: 'nowrap' };
const tdFirst = { height: '41px', paddingTop: '7px', paddingRight: '9px', paddingBottom: '7px', paddingLeft: '4px', fontSize: '13px', color: COLORS.text, fontWeight: 500, borderBottom: `1px solid ${COLORS.borderSoft}` };
const inp = { ...uiInput };
// 탭 단추(시안 .view-tab) — 색은 var(--gw-…) 라 다크에서도 따라온다. PillTabs(role=tab)로 바꾸지 않는다(단추 이름으로 찾는 시험).
const pillTab = (on) => ({
  padding: '5px 10px', borderRadius: '18px', fontSize: '13px', border: 0, cursor: 'pointer', whiteSpace: 'nowrap',
  background: on ? `var(--gw-gray-soft, ${TONES.gray.soft})` : 'transparent',
  color: on ? `var(--gw-text, ${COLORS.text})` : `var(--gw-muted, ${COLORS.textMuted})`,
  fontWeight: on ? 500 : 400,
});
// 유형·기준·도급사 칩 — 단추 껍데기는 비우고 모양은 Pill 이 맡는다(gw-btn 은 disabled 투명도만)
const chipBtn = { background: 'none', border: 0, padding: 0, cursor: 'pointer' };

export default function CenterDailyPage() {
  const [centers, setCenters] = useState([]);
  const [center, setCenter] = useState('');
  const [date, setDate] = useState(yesterday());
  const [tab, setTab] = useState('input');       // input | report
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [labor, setLabor] = useState({});
  const [ship, setShip] = useState({});          // {shipper_id: {normal, guarantee}}
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [history, setHistory] = useState(null);  // null = 아직 안 읽음
  // 도급사별 인원 — 목록·단가는 GET /contractors(카드 조건일 때만), 입력은 {contractor_id: {grade: {persons, ot}}}
  const [conList, setConList] = useState(null);
  const [conDayKind, setConDayKind] = useState('평일');
  const [conSel, setConSel] = useState(null);
  const [conInput, setConInput] = useState({});
  // 도급사·단가 관리(관리자) — 펼칠 때만 읽는다
  const [admOpen, setAdmOpen] = useState(false);
  const [adm, setAdm] = useState(null);
  const [admName, setAdmName] = useState('');
  const [admNames, setAdmNames] = useState({});  // {id: 고치는 중인 이름}
  const [admSel, setAdmSel] = useState(null);
  const [admRates, setAdmRates] = useState({});  // {grade: {weekday, holiday}}
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    centerShipperAPI.myCenters().then((r) => {
      const cs = r.data?.centers || [];
      setCenters(cs);
      if (cs.length) setCenter((p) => p || cs[0].center_key);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!center || !date) return;
    try {
      if (tab === 'input') {
        const r = await centerDailyAPI.get(center, date);
        setData(r.data);
        setLabor(r.data.labor || {});
        setShip(Object.fromEntries((r.data.items || []).map((i) => [i.shipper_id, { normal: i.normal_cnt, guarantee: i.guarantee_cnt }])));
        setConInput(conInputFrom(r.data.contractor_rows));
        setConDayKind(r.data.day_kind || r.data.day_kind_default || '평일');
        setDirty(false);
      } else if (tab === 'report') {
        const r = await centerDailyAPI.report(center, date);
        setReport(r.data);
      }
    } catch (e) {
      if (e.response?.status === 403) alertDialog('이 센터에 접근할 권한이 없습니다.');
      setData(null); setReport(null);
    }
  }, [center, date, tab]);

  useEffect(() => { load(); }, [load]);

  // 입력량이 많아 실수로 날리면 다시 적어야 한다
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  // 센터·날짜가 바뀌면 이력은 접고 비운다(다른 날의 이력을 보여 주지 않게) — API 호출은 없다
  useEffect(() => { setHistOpen(false); setHistory(null); }, [center, date]);
  // 센터가 바뀌면 도급사 목록·관리 카드도 비운다(다른 센터 도급사를 보여 주지 않게) — API 호출은 없다
  useEffect(() => { setConList(null); setConSel(null); setAdmOpen(false); setAdm(null); setAdmNames({}); }, [center]);

  // 🔴 «도급사별 인원» 카드 조건 — has_contractors 는 활성 도급사만 센다. 전부 «사용 안 함»이 된 뒤에도
  //    그날 행이 있으면 카드를 보여야 고칠 수 있다. 도급사 없는 센터는 contractors 를 부르지 않는다.
  const showConGrid = tab === 'input' && !!data && data.center?.allow_labor !== false
    && (!!data.has_contractors || (data.contractor_rows || []).length > 0);
  useEffect(() => {
    if (!showConGrid) return undefined;
    let alive = true;
    (async () => {
      try {
        const r = await centerDailyAPI.contractors(data.center?.center_key || center);
        if (alive) setConList(r?.data || EMPTY_CON);
      } catch (e) {
        if (alive) setConList({ ...EMPTY_CON, failed: true });
      }
    })();
    return () => { alive = false; };
  }, [showConGrid, data]);

  // 관리 카드 목록이 새로 오면 고른 도급사(없으면 첫 도급사)의 단가표를 다시 채운다
  useEffect(() => {
    if (!adm) return;
    const ks = adm.contractors || [];
    const k = ks.find((x) => x.id === admSel) || ks[0] || null;
    setAdmSel(k ? k.id : null);
    setAdmRates(ratesForm(adm.grades, k));
  }, [adm]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await centerDailyAPI.history(center, date);
      setHistory(r.data?.items || []);
    } catch (e) {
      setHistory([]);
      alertDialog('수정 이력을 불러오지 못했습니다: ' + (e.response?.data?.detail || e.message));
    }
  }, [center, date]);

  const toggleHistory = () => {
    const next = !histOpen;
    setHistOpen(next);
    if (next) loadHistory();
  };

  // 저장·마감·해제 뒤 — 이력이 펼쳐져 있을 때만 다시 읽는다
  const afterChange = () => { load(); if (histOpen) loadHistory(); };

  const closed = data?.closed || null;            // 응답에 키가 없으면 마감 전으로 본다
  const locked = !!closed;
  const noteTypes = data?.note_types || DEFAULT_NOTE_TYPES;
  const errText = (e, label) => (e.response?.status === 409
    ? e.response.data?.detail || label
    : `${label}: ${e.response?.data?.detail || e.message}`);

  // 그날 도급사 행이 있으면 도급 3칸의 정본은 도급사 입력 — 평면 저장은 그 칸을 null(=안 건드림)로 보낸다
  const byContractor = (data?.contractor_rows || []).length > 0;

  const saveLabor = async () => {
    setBusy(true);
    try {
      const body = { center_key: center, work_date: date, ...labor, note_type: labor.note_type || null };
      if (byContractor) { body.con_persons = null; body.con_hours = null; body.con_cost = null; }
      const r = await centerDailyAPI.putLabor(body);
      toast(r.data.cost_is_default ? '저장했습니다(도급비는 센터 기본값이 적용됨).' : '저장했습니다.');
      setDirty(false); afterChange();
    } catch (e) { alertDialog(errText(e, '저장 실패')); }
    finally { setBusy(false); }
  };

  const setConCell = (cid, grade, field, value) => {
    setConInput((p) => {
      const cur = p[cid]?.[grade] || { persons: '', ot: '' };
      return { ...p, [cid]: { ...(p[cid] || {}), [grade]: { ...cur, [field]: value } } };
    });
    setDirty(true);
  };

  // 🔴 rows 는 모든 도급사(비활성 도급사의 그날 행 포함) — 서버가 그날 행을 이 몸통으로 통째 바꾼다
  const saveConLabor = async () => {
    setBusy(true);
    try {
      await centerDailyAPI.putContractorLabor({ center_key: center, work_date: date, day_kind: conDayKind, rows: conRowsOut(conInput) });
      toast('저장했습니다.'); setDirty(false); afterChange();
    } catch (e) { alertDialog(errText(e, '저장 실패')); }
    finally { setBusy(false); }
  };

  // ── 도급사·단가 관리(관리자) ──
  const loadAdm = async () => {
    try {
      const r = await centerDailyAPI.contractors(center);
      setAdm(r?.data || EMPTY_CON);
    } catch (e) {
      setAdm(EMPTY_CON);
      alertDialog(errText(e, '도급사 목록을 불러오지 못했습니다'));
    }
  };
  const toggleAdm = () => {
    const next = !admOpen;
    setAdmOpen(next);
    if (next) loadAdm();
  };
  // 저장·추가 뒤 — 목록을 다시 읽고 입력 탭도 다시 읽는다(has_contractors·단가가 바뀐다)
  const admRun = async (fn, okMsg, label) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast(okMsg);
      await loadAdm();
      load();
    } catch (e) { alertDialog(errText(e, label)); }
    finally { setBusy(false); }
  };
  const addCon = () => {
    const name = admName.trim();
    if (!name) return;
    admRun(async () => { await centerDailyAPI.addContractor({ center_key: center, name }); setAdmName(''); }, '도급사를 추가했습니다.', '추가 실패');
  };
  const renameCon = (k, name) => admRun(async () => {
    await centerDailyAPI.updateContractor(k.id, { name: name.trim() });
    setAdmNames((p) => { const n = { ...p }; delete n[k.id]; return n; });
  }, '이름을 고쳤습니다.', '이름 저장 실패');
  const toggleConActive = (k) => admRun(() => centerDailyAPI.updateContractor(k.id, { active: !k.active }),
    k.active ? '사용 안 함으로 바꿨습니다.' : '사용으로 바꿨습니다.', '상태 변경 실패');
  // 🔴 도급사는 sort_order=0 으로 만들어진다 — 한 칸만 PATCH 하면 순서가 안 바뀐다.
  //    목록 전체를 새 순서대로 0..n-1 로 다시 매기고, 값이 바뀐 도급사만 PATCH 한다.
  const moveCon = (idx, dir) => {
    const ks = [...(adm?.contractors || [])];
    const j = idx + dir;
    if (j < 0 || j >= ks.length) return;
    [ks[idx], ks[j]] = [ks[j], ks[idx]];
    admRun(async () => {
      for (let i = 0; i < ks.length; i += 1) {
        if (ks[i].sort_order !== i) await centerDailyAPI.updateContractor(ks[i].id, { sort_order: i });
      }
    }, null, '순서 변경 실패');
  };
  const pickAdm = (k) => { setAdmSel(k.id); setAdmRates(ratesForm(adm?.grades, k)); };
  const saveRates = () => {
    const cid = admSel;
    const rates = (adm?.grades || []).map((g) => ({ grade: g, weekday_rate: rateIn(admRates[g]?.weekday), holiday_rate: rateIn(admRates[g]?.holiday) }));
    admRun(() => centerDailyAPI.putRates(cid, { rates }), '단가를 저장했습니다.', '단가 저장 실패');
  };

  const saveShip = async () => {
    setBusy(true);
    try {
      const rows = Object.entries(ship).map(([id, v]) => ({ shipper_id: Number(id), normal_cnt: Number(v.normal) || 0, guarantee_cnt: Number(v.guarantee) || 0 }));
      await centerDailyAPI.putShipment({ center_key: center, work_date: date, rows });
      toast('출고를 저장했습니다.'); setDirty(false); afterChange();
    } catch (e) { alertDialog(errText(e, '저장 실패')); }
    finally { setBusy(false); }
  };

  const resync = async () => {
    setBusy(true);
    try {
      const r = await centerDailyAPI.resync({ center_key: center, work_date: date });
      const d = r.data;
      toast(d.error ? `집계 실패: ${d.error}` : d.skipped ? `집계 안 함: ${d.skipped}` : `자동 ${d.auto}건 반영 · 수기 보존 ${d.kept_manual || 0} · 미매핑 ${d.unmapped}곳`);
      load();
    } catch (e) { alertDialog(errText(e, '재집계 실패')); }
    finally { setBusy(false); }
  };

  const closeDay = async () => {
    const msg = `${date} 를 마감합니다. 마감 후에는 근무·출고를 고칠 수 없고 자동 집계도 멈춥니다.`
      + (dirty ? '\n저장하지 않은 입력은 반영되지 않습니다.' : '');
    if (!(await confirmDialog(msg))) return;
    setBusy(true);
    try {
      await centerDailyAPI.close({ center_key: center, work_date: date });
      toast('마감했습니다.'); setDirty(false); afterChange();
    } catch (e) { alertDialog(errText(e, '마감 실패')); }
    finally { setBusy(false); }
  };

  const reopenDay = async () => {
    if (!(await confirmDialog(`${date} 마감을 해제합니다. 해제하면 근무·출고를 다시 고칠 수 있습니다.`))) return;
    setBusy(true);
    try {
      await centerDailyAPI.reopen({ center_key: center, work_date: date });
      toast('마감을 해제했습니다.'); afterChange();
    } catch (e) { alertDialog(errText(e, '마감 해제 실패')); }
    finally { setBusy(false); }
  };

  // 🔴 링크로 열면 토큰이 안 실려 인증 오류가 난다 → blob 으로 받아 저장(고객사 명부 양식과 동일)
  const downloadReport = async () => {
    try {
      const r = await centerDailyAPI.exportReport(center, date);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${center}_일별출고_${date}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { alertDialog('내려받기에 실패했습니다: ' + (e.response?.data?.detail || e.message)); }
  };

  const meta = data?.center || report?.center;

  const conKs = conList?.contractors || [];
  const conRowKeys = new Set((data?.contractor_rows || []).map((r) => `${r.contractor_id}|${r.grade}`));
  const conRowCids = new Set((data?.contractor_rows || []).map((r) => r.contractor_id));
  // 단추 = 활성 도급사(sort_order 순) + 그날 행이 있는 비활성 도급사(«사용 안 함»)
  const conButtons = [...conKs.filter((k) => k.active), ...conKs.filter((k) => !k.active && conRowCids.has(k.id))];
  const conK = conButtons.find((k) => k.id === conSel) || conButtons[0] || null;
  const admList = adm?.contractors || [];
  const admK = admList.find((k) => k.id === admSel) || null;

  return (
    <AppShell app="center-daily">
      {/* 🔴 본문은 사이드바의 **형제**다 — AppSidebar 의 children 은 사이드바 아코디언용이라
          거기에 넣으면 본문이 사이드바 안에서 열린다(레포 규약: CenterCostPage·DirectoryPage 참조). */}
      <div style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto' }}>
      <Page>
        <PageHeader
          icon={<IconTrendingUp aria-hidden="true" />}
          title="센터 일별 실적"
          subtitle="근무·도급은 직접 입력하고, 2.0 고객사 출고는 매일 06:00 자동 집계됩니다(당일 05시~익일 04시59분 기준)."
        />

        {/* 탭 줄 — 선(.gw-tabs)이 줄 전체 폭에 그어지도록 필터 줄 밖에 둔다. 인라인 style 은 주지 않는다(≤600 값은 index.html) */}
        <div className="gw-tabs">
          {[['input', '입력'], ['report', '실적요약']].map(([k, label]) => (
            <button key={k} type="button" className="gw-tab" aria-pressed={tab === k} onClick={() => setTab(k)} style={pillTab(tab === k)}>{label}</button>
          ))}
        </div>

        <div data-testid="center-daily-filters" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
          <select value={center} onChange={(e) => setCenter(e.target.value)} style={{ ...inp, width: 'auto', minWidth: '170px' }}>
            {centers.map((c) => <option key={c.center_key} value={c.center_key}>{c.display_name}{c.temp_zone ? ` (${c.temp_zone})` : ''}</option>)}
          </select>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...inp, width: 'auto' }} />
          <div style={{ flex: 1 }} />
          {tab === 'input' && data && (locked ? (
            <>
              <span data-testid="center-daily-closed">
                <Pill tone="green">마감됨 · {closed.closer_name || '—'} · {fmtKstDateTime(closed.closed_at) || '—'}</Pill>
              </span>
              <Button variant="secondary" onClick={reopenDay} disabled={busy}>마감 해제</Button>
            </>
          ) : (
            // 시안 밖 채움색 — Button 의 primary hover(!important)가 바탕을 덮으므로 gw-btn 만 달고 직접 그린다
            <button type="button" className="gw-btn" onClick={closeDay} disabled={busy} style={{ ...uiBtn('primary'), background: COLORS.warn }}>마감</button>
          ))}
          {tab === 'input' && <Button variant="secondary" icon={<IconRefresh aria-hidden="true" />} onClick={resync} disabled={busy || locked}>자동 재집계</Button>}
          {tab === 'report' && (
            <button type="button" className="gw-btn" onClick={downloadReport}
              style={{ ...uiBtn('secondary'), background: COLORS.successSoft, color: COLORS.successText, borderColor: 'transparent', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <IconDownload size={15} stroke={ICON_STROKE} aria-hidden="true" />엑셀 내려받기
            </button>
          )}
        </div>

        {tab === 'input' && data && (
          <>
            {locked && (
              <div style={{ ...card, background: COLORS.successSoft, color: COLORS.successText, fontSize: '12px' }}>
                마감된 날짜입니다 — 근무·출고를 고칠 수 없고 자동 집계도 덮지 않습니다. 고치려면 «마감 해제»를 누르세요.
              </div>
            )}
            {meta?.allow_labor === false && (
              <div style={{ ...card, background: COLORS.warnSoft, color: COLORS.text, fontSize: '12px' }}>
                위탁센터라 도급(인건) 입력은 받지 않습니다. 출고만 관리합니다.
              </div>
            )}
            {meta?.allow_labor !== false && (
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <strong style={{ fontSize: '14px', fontWeight: 620, color: COLORS.text }}>근무 · 도급</strong>
                  <span style={{ fontSize: '11px', color: COLORS.textMuted }}>
                    {data.labor?.updater_name ? `최종 수정 ${data.labor.updater_name}` : '미입력'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                  {[['reg_persons', '정규 인원'], ['con_persons', '도급 인원'], ['reg_hours', '정규 시간(M.H)'], ['con_hours', '도급 시간(M.H)'], ['con_cost', '도급 금액']].map(([k, label]) => (
                    <div key={k}>
                      <div style={{ fontSize: '11px', color: COLORS.textSub, marginBottom: '3px' }}>{label}</div>
                      <input type="number" aria-label={label} value={num(labor[k])}
                        disabled={locked || (byContractor && k.startsWith('con_'))}
                        onChange={(e) => { setLabor({ ...labor, [k]: e.target.value === '' ? null : Number(e.target.value) }); setDirty(true); }} style={inp} />
                    </div>
                  ))}
                </div>
                {byContractor ? (
                  <div style={{ fontSize: '11px', color: COLORS.textMuted, margin: '6px 0 8px' }}>이 날은 도급사별로 입력했습니다 — 도급 인원·시간·금액은 아래 «도급사별 인원»에서 고칩니다.</div>
                ) : (
                  <div style={{ fontSize: '11px', color: COLORS.textMuted, margin: '6px 0 8px' }}>
                    도급 금액을 비우면 센터 기본값({(data.default_cost || 0).toLocaleString()}원)이 적용됩니다
                    {data.labor?.cost_is_default && ' — 현재 값은 기본값입니다'}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: COLORS.textSub, marginBottom: '3px' }}>운영 특이사항(실적요약에 그대로 들어갑니다)</div>
                {/* 유형은 단추 — 누른 것만 강조, 다시 누르면 해제(<select> 로 만들지 않는다) */}
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '5px' }}>
                  <span style={{ fontSize: '11px', color: COLORS.textMuted, marginRight: '2px' }}>유형</span>
                  {noteTypes.map((t) => {
                    const on = labor.note_type === t;
                    return (
                      <button key={t} type="button" aria-pressed={on} disabled={locked}
                        onClick={() => { setLabor({ ...labor, note_type: on ? null : t }); setDirty(true); }}
                        className="gw-btn" style={chipBtn}><Pill tone={on ? 'blue' : 'gray'}>{t}</Pill></button>
                    );
                  })}
                </div>
                <textarea value={labor.note || ''} disabled={locked} onChange={(e) => { setLabor({ ...labor, note: e.target.value }); setDirty(true); }} rows={2} style={{ ...inp, resize: 'vertical' }} />
                <div style={{ marginTop: '8px' }}>
                  <Button variant="primary" onClick={saveLabor} disabled={busy || locked}>근무·도급 저장</Button>
                </div>
              </div>
            )}

            {showConGrid && (
              <div style={card}>
                {/* 기준·도급사도 단추 — <select> 로 만들지 않는다(입력 탭의 select 는 센터 하나뿐) */}
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <strong style={{ fontSize: '14px', fontWeight: 620, color: COLORS.text, marginRight: '8px' }}>도급사별 인원</strong>
                  <span style={{ fontSize: '11px', color: COLORS.textMuted, marginRight: '2px' }}>기준</span>
                  {['평일', '휴일'].map((dk) => {
                    const on = conDayKind === dk;
                    return (
                      <button key={dk} type="button" aria-pressed={on} disabled={locked}
                        onClick={() => { setConDayKind(dk); setDirty(true); }}
                        className="gw-btn" style={chipBtn}><Pill tone={on ? 'blue' : 'gray'}>{dk}</Pill></button>
                    );
                  })}
                  <span style={{ fontSize: '11px', color: COLORS.textMuted, margin: '0 2px 0 10px' }}>도급사</span>
                  {conButtons.map((k) => {
                    const on = conK?.id === k.id;
                    return (
                      <button key={k.id} type="button" aria-pressed={on} onClick={() => setConSel(k.id)}
                        className="gw-btn" style={chipBtn}>
                        <Pill tone={on ? 'blue' : 'gray'}>{k.active ? k.name : `${k.name} (사용 안 함)`}</Pill>
                      </button>
                    );
                  })}
                </div>
                {conList === null ? (
                  <div style={{ fontSize: '12px', color: COLORS.textMuted }}>불러오는 중…</div>
                ) : conList.failed ? (
                  <div style={{ fontSize: '12px', color: COLORS.warn }}>도급사 목록을 불러오지 못했습니다.</div>
                ) : !conK ? (
                  <div style={{ fontSize: '12px', color: COLORS.textMuted }}>사용 중인 도급사가 없습니다.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table data-testid="con-grid" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px' }}>
                      <thead><tr>{['구분', '인원(명)', '연장(시간)', '단가', '금액(미리보기)'].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {(conList.grades || []).map((g) => {
                          const v = conInput[conK.id]?.[g] || { persons: '', ot: '' };
                          const rate = rateOf(conK, g, conDayKind);
                          const hasVal = conRowKeys.has(`${conK.id}|${g}`) || v.persons !== '' || v.ot !== '';
                          // 🔴 비활성 도급사는 그날 행이 있는 구분만, 단가 없는 구분은 막는다 — 보내면 서버가 저장 전체를 400 으로 거부한다.
                          //    값이 들어 있는 칸은 지울 수 있게 열어 둔다(저장하면 서버 문구가 그대로 뜬다).
                          const off = locked || (!(conK.active && rate !== null) && !hasVal);
                          return (
                            <tr key={g} className="gw-tr">
                              <td style={{ ...tdFirst, whiteSpace: 'nowrap' }}>{g}</td>
                              <td style={{ ...td, width: '110px' }}>
                                <input type="number" min={0} step={1} aria-label={`${conK.name} ${g} 인원`} value={v.persons} disabled={off}
                                  onChange={(e) => setConCell(conK.id, g, 'persons', e.target.value)} style={inp} />
                              </td>
                              <td style={{ ...td, width: '150px' }}>
                                <input type="text" aria-label={`${conK.name} ${g} 연장`} value={v.ot} disabled={off} placeholder="예: =19*5-1*1"
                                  onChange={(e) => setConCell(conK.id, g, 'ot', e.target.value)} style={inp} />
                              </td>
                              <td data-testid={`con-rate-${g}`} style={{ ...td, whiteSpace: 'nowrap', color: rate === null ? COLORS.textMuted : COLORS.text }}>
                                {rate === null ? '단가 없음' : `${Number(rate).toLocaleString()}원`}
                              </td>
                              <td data-testid={`con-cost-${g}`} style={{ ...td, whiteSpace: 'nowrap', fontSize: '12px', color: COLORS.textSub }}>{costPreview(v, rate)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <div data-testid="con-totals" style={{ fontSize: '12px', color: COLORS.textSub, margin: '8px 0' }}>
                  저장된 합계 · 도급 인원 {dash(data.labor?.con_persons)}명 · 도급 시간 {dash(data.labor?.con_hours)} · 도급 금액 {typeof data.labor?.con_cost === 'number' ? `${data.labor.con_cost.toLocaleString()}원` : '—'}
                </div>
                <Button variant="primary" onClick={saveConLabor} disabled={busy || locked || conList === null || !!conList.failed}>도급사별 저장</Button>
              </div>
            )}

            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <strong style={{ fontSize: '14px', fontWeight: 620, color: COLORS.text }}>고객사 출고</strong>
                <span style={{ fontSize: '12px', color: COLORS.textSub }}>
                  일반 {data.totals.normal.toLocaleString()} · 도착보장 {data.totals.guarantee.toLocaleString()}
                  {data.pending_count > 0 && <span style={{ color: COLORS.warn }}> · 미집계 {data.pending_count}곳</span>}
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px' }}>
                  <thead><tr>{['고객사', '시스템', '일반출고', '도착보장', '출처'].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {data.items.map((it) => {
                      // 상태색은 tr 이 아니라 각 td 에 — tr 바탕은 .gw-tr:hover>td 에 가려진다(매핑 필요 행은 hover 에도 색 유지)
                      const rowBg = it.needs_mapping ? COLORS.dangerSoft : undefined;
                      return (
                        <tr key={it.shipper_id} className="gw-tr">
                          <td style={{ ...tdFirst, background: rowBg }}>{it.display_name}{it.temp_zone ? <span style={{ fontSize: '11px', color: COLORS.textMuted }}> ({it.temp_zone})</span> : null}</td>
                          <td style={{ ...td, background: rowBg }}>{it.system_ver}</td>
                          {['normal', 'guarantee'].map((f) => (
                            <td key={f} style={{ ...td, width: '110px', background: rowBg }}>
                              <input type="number" value={num(ship[it.shipper_id]?.[f])} disabled={locked} onChange={(e) => { setShip({ ...ship, [it.shipper_id]: { ...ship[it.shipper_id], [f]: e.target.value } }); setDirty(true); }} style={inp} />
                            </td>
                          ))}
                          <td style={{ ...td, fontSize: '11px', color: COLORS.textMuted, background: rowBg }}>
                            {!it.collected ? <span style={{ color: COLORS.warn }}>미집계</span>
                              : it.source === 'auto' ? '자동' : `수기${it.updater_name ? ` · ${it.updater_name}` : ''}`}
                          </td>
                        </tr>
                      );
                    })}
                    {!data.items.length && <tr><td style={{ ...td, color: COLORS.textMuted, textAlign: 'center' }} colSpan={5}>등록된 고객사가 없습니다. '센터 고객사 명부'에서 먼저 등록하세요.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Button variant="primary" onClick={saveShip} disabled={busy || locked || !data.items.length}>출고 저장</Button>
                <span style={{ fontSize: '11px', color: COLORS.textMuted }}>자동 집계된 값을 고친 행만 수기로 바뀌어 이후 배치가 덮지 않습니다.</span>
              </div>
            </div>

            <div style={card}>
              {/* 접기 단추 = 시안 .quiet(.btn 아님) — 아이콘은 svg.i 18px · -8px 로 시작선을 카드 제목과 맞춘다 */}
              <Button variant="quiet" onClick={toggleHistory} aria-expanded={histOpen} style={{ marginLeft: '-8px' }}
                icon={histOpen ? <IconChevronDown size={18} aria-hidden="true" /> : <IconChevronRight size={18} aria-hidden="true" />}>
                수정 이력
              </Button>
              {histOpen && (
                history === null ? <div style={{ marginTop: '8px', fontSize: '12px', color: COLORS.textMuted }}>불러오는 중…</div>
                  : !history.length ? <div style={{ marginTop: '8px', fontSize: '12px', color: COLORS.textMuted }}>수정 이력이 없습니다</div>
                    : (
                      <div data-testid="center-daily-history" style={{ overflowX: 'auto', marginTop: '8px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px' }}>
                          <thead><tr>{['시각(KST)', '사람', '구분', '대상', '이전 → 이후'].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr></thead>
                          <tbody>
                            {history.map((h, i) => (
                              <tr key={i} className="gw-tr">
                                <td style={{ ...tdFirst, whiteSpace: 'nowrap' }}>{fmtKstDateTime(h.created_at) || '—'}</td>
                                <td style={td}>{h.actor_name || '—'}</td>
                                <td style={td}>{KIND_LABEL[h.kind] || h.kind}</td>
                                <td style={td}>{histTarget(h)}</td>
                                <td style={td}>{histChange(h)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
              )}
            </div>

            {/* 도급사·단가 관리 — 관리자만(서버도 403). 마감과 무관(단가는 날짜에 안 묶인다 — 저장 시점 스냅샷). 삭제 API 는 없다 */}
            {isAdmin && meta?.allow_labor !== false && (
              <div style={card}>
                <Button variant="quiet" onClick={toggleAdm} aria-expanded={admOpen} style={{ marginLeft: '-8px' }}
                  icon={admOpen ? <IconChevronDown size={18} aria-hidden="true" /> : <IconChevronRight size={18} aria-hidden="true" />}>
                  도급사·단가 관리(관리자)
                </Button>
                {admOpen && (adm === null ? <div style={{ marginTop: '8px', fontSize: '12px', color: COLORS.textMuted }}>불러오는 중…</div> : (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '10px' }}>
                      <input aria-label="새 도급사 이름" placeholder="새 도급사 이름" value={admName} onChange={(e) => setAdmName(e.target.value)} style={{ ...inp, width: '220px' }} />
                      <Button variant="primary" onClick={addCon} disabled={busy || !admName.trim()}>추가</Button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px' }}>
                        <thead><tr>{['순서', '이름', '상태', ''].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr></thead>
                        <tbody>
                          {admList.map((k, i) => {
                            const nm = admNames[k.id] ?? k.name;
                            return (
                              <tr key={k.id} className="gw-tr">
                                <td style={{ ...tdFirst, whiteSpace: 'nowrap', width: '80px' }}>
                                  <Button variant="quiet" icon={<IconArrowUp aria-hidden="true" />} aria-label={`${k.name} 위로`} onClick={() => moveCon(i, -1)} disabled={busy || i === 0} style={{ marginRight: '3px' }} />
                                  <Button variant="quiet" icon={<IconArrowDown aria-hidden="true" />} aria-label={`${k.name} 아래로`} onClick={() => moveCon(i, 1)} disabled={busy || i === admList.length - 1} />
                                </td>
                                <td style={td}>
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    <input aria-label={`${k.name} 이름`} value={nm} onChange={(e) => setAdmNames({ ...admNames, [k.id]: e.target.value })} style={{ ...inp, width: '200px' }} />
                                    <Button variant="quiet" onClick={() => renameCon(k, nm)} disabled={busy || !nm.trim() || nm.trim() === k.name}>이름 저장</Button>
                                  </div>
                                </td>
                                <td style={{ ...td, color: k.active ? COLORS.text : COLORS.textMuted }}>{k.active ? '사용' : '사용 안 함'}</td>
                                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                  <Button variant="secondary" aria-label={`${k.name} ${k.active ? '사용 안 함' : '사용'}`} onClick={() => toggleConActive(k)} disabled={busy} style={{ marginRight: '4px' }}>{k.active ? '사용 안 함' : '사용'}</Button>
                                  <Button variant={admK?.id === k.id ? 'primary' : 'quiet'} aria-label={`${k.name} 단가표`} aria-pressed={admK?.id === k.id} onClick={() => pickAdm(k)}>단가표</Button>
                                </td>
                              </tr>
                            );
                          })}
                          {!admList.length && <tr><td style={{ ...td, color: COLORS.textMuted, textAlign: 'center' }} colSpan={4}>등록된 도급사가 없습니다.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                    {admK && (
                      <div style={{ marginTop: '12px' }}>
                        <strong style={{ fontSize: '13px' }}>{admK.name} 단가표</strong>
                        <span style={{ fontSize: '11px', color: COLORS.textMuted, marginLeft: '6px' }}>빈 칸 = 그 기준 미사용 · 이미 저장한 날의 금액은 바뀌지 않습니다</span>
                        <div style={{ overflowX: 'auto', marginTop: '6px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
                            <thead><tr>{['구분', '평일 단가', '휴일 단가'].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr></thead>
                            <tbody>
                              {(adm.grades || []).map((g) => (
                                <tr key={g} className="gw-tr">
                                  <td style={{ ...tdFirst, whiteSpace: 'nowrap' }}>{g}</td>
                                  {[['weekday', '평일'], ['holiday', '휴일']].map(([f, label]) => (
                                    <td key={f} style={{ ...td, width: '160px' }}>
                                      <input type="number" min={0} aria-label={`${admK.name} ${g} ${label} 단가`} value={num(admRates[g]?.[f])}
                                        onChange={(e) => setAdmRates({ ...admRates, [g]: { ...(admRates[g] || {}), [f]: e.target.value } })} style={inp} />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ marginTop: '8px' }}>
                          <Button variant="primary" onClick={saveRates} disabled={busy}>단가 저장</Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'report' && report && (
          <>
            {/* 대시보드 기준일 = 마감된 날(노션 [대시보드]). closed 키가 없으면(구버전 응답) 표시하지 않는다 */}
            {report.closed === false && (
              <div data-testid="report-not-closed" style={{ ...card, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '10px 14px' }}>
                <span style={{ color: COLORS.warn, fontWeight: 500, fontSize: '13px' }}>이 날짜는 아직 마감 전입니다</span>
                {report.last_closed_date && report.last_closed_date !== report.base_date && (
                  <Button variant="secondary" onClick={() => setDate(report.last_closed_date)}>
                    마감 기준일({report.last_closed_date})로 보기
                  </Button>
                )}
              </div>
            )}
            {report.closed === true && (
              <div style={{ ...card, padding: '10px 14px', fontSize: '12px', color: COLORS.successText, background: COLORS.successSoft }}>마감된 날짜입니다</div>
            )}
            <div style={card}>
              <strong style={{ fontSize: '14px', fontWeight: 620, color: COLORS.text }}>(1) 근무인원</strong>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                <thead><tr>{['구분', '당일', '당월누적'].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr></thead>
                <tbody>
                  <tr className="gw-tr"><td style={tdFirst}>정규 인원</td><td style={td}>{dash(report.labor.day?.reg_persons)}</td><td style={td}>{report.labor.month.reg_persons}</td></tr>
                  <tr className="gw-tr"><td style={tdFirst}>도급 인원</td><td style={td}>{dash(report.labor.day?.con_persons)}</td><td style={td}>{report.labor.month.con_persons}</td></tr>
                  <tr className="gw-tr"><td style={tdFirst}>도급비</td><td style={td}>{report.labor.day ? report.labor.day.con_cost.toLocaleString() : '—'}{report.labor.day?.cost_is_default && <span style={{ fontSize: '11px', color: COLORS.textMuted }}> (기본값)</span>}</td><td style={td}>{report.labor.month.con_cost.toLocaleString()}</td></tr>
                </tbody>
              </table>
            </div>
            <div style={card}>
              <strong style={{ fontSize: '14px', fontWeight: 620, color: COLORS.text }}>생산성</strong>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                <thead><tr>{['기준일', '생산성/인', '전주대비', '생산성/M.H', '전주대비'].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr></thead>
                <tbody>
                  {report.productivity.weeks.map((w) => (
                    <tr key={w.date} className="gw-tr">
                      <td style={tdFirst}>{w.date}</td><td style={td}>{dash(w.per_person)}</td><td style={td}>{dash(w.per_person_delta)}</td>
                      <td style={td}>{dash(w.per_mh)}</td><td style={td}>{dash(w.per_mh_delta)}</td>
                    </tr>
                  ))}
                  <tr className="gw-tr"><td style={tdFirst}>당월누적</td><td style={td}>{dash(report.productivity.month.per_person)}</td><td style={td} /><td style={td}>{dash(report.productivity.month.per_mh)}</td><td style={td} /></tr>
                </tbody>
              </table>
            </div>
            <div style={card}>
              <strong style={{ fontSize: '14px', fontWeight: 620, color: COLORS.text }}>(2) 운영 특이사항</strong>
              <div style={{ marginTop: '6px', fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                {report.labor.note_type && (
                  <span style={{ marginRight: '6px' }}><Pill tone="blue">[{report.labor.note_type}]</Pill></span>
                )}
                {report.labor.note || '특이사항 없음'}
              </div>
            </div>
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <strong style={{ fontSize: '14px', fontWeight: 620, color: COLORS.text }}>(3) 출고실적</strong>
                <span style={{ fontSize: '11px', color: COLORS.textMuted }}>
                  자동 {report.quality.auto_count} · 수기 {report.quality.manual_count}
                  {report.quality.pending_count > 0 && <span style={{ color: COLORS.warn }}> · 미집계 {report.quality.pending_count}</span>}
                  {report.quality.labor_missing && <span style={{ color: COLORS.warn }}> · 근무 미입력</span>}
                </span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                <thead><tr>{['고객사', '일반(당일)', '일반(누적)', '보장(당일)', '보장(누적)'].map((h, i) => <th key={h} style={i === 0 ? thFirst : th}>{h}</th>)}</tr></thead>
                <tbody>
                  <tr className="gw-tr" style={{ fontWeight: 500 }}>
                    <td style={tdFirst}>소계</td><td style={td}>{report.shipment.day.normal.toLocaleString()}</td><td style={td}>{report.shipment.month.normal.toLocaleString()}</td>
                    <td style={td}>{report.shipment.day.guarantee.toLocaleString()}</td><td style={td}>{report.shipment.month.guarantee.toLocaleString()}</td>
                  </tr>
                  {report.shipment.items.map((it, i) => (
                    <tr key={i} className="gw-tr">
                      <td style={tdFirst}>{it.display_name}</td><td style={td}>{it.day_normal.toLocaleString()}</td><td style={td}>{it.month_normal.toLocaleString()}</td>
                      <td style={td}>{it.day_guarantee.toLocaleString()}</td><td style={td}>{it.month_guarantee.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Page>
      </div>
    </AppShell>
  );
}
